import { fixedUi } from '@/i18n/fixedUi';
import React from 'react';
import TiktokDateTimePicker from '@/tiktok-platform/modules/products/components/TiktokDateTimePicker';

/** 售后常用：申请 / 签收 / 完成 */
export const TIKTOK_RETURN_TIME_TYPE_OPTIONS = [
  { value: 'apply', get label() { return fixedUi('申请'); } },
  { value: 'signed', get label() { return fixedUi('签收'); } },
  { value: 'completed', get label() { return fixedUi('完成'); } },
];

/** 从 yyyy-MM-dd 或带时间的串中取出日期部分 */
export function pickDatePart(value) {
  const match = String(value || '').trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

/** 本地库按日筛选：日起始 */
export function toDayStartDateTime(dateStr) {
  const day = pickDatePart(dateStr);
  return day ? `${day} 00:00:00` : '';
}

/** 本地库按日筛选：日结束 */
export function toDayEndDateTime(dateStr) {
  const day = pickDatePart(dateStr);
  return day ? `${day} 23:59:59` : '';
}

/**
 * 校验开始/结束日期（仅比较 yyyy-MM-dd）。
 * @returns {string} 错误文案；合法时返回空串
 */
export function validateDateRange(startDate, endDate) {
  const start = pickDatePart(startDate);
  const end = pickDatePart(endDate);
  if (start && end && start > end) {
    return fixedUi('开始日期必须早于或等于结束日期');
  }
  return '';
}

/**
 * 筛选栏日期范围查询：时间类型下拉 + 开始/结束日期。
 * 默认仅选日期（不含时分），便于本地库按日过滤。
 *
 * @param {{
 *   timeType?: string,
 *   startDate?: string,
 *   endDate?: string,
 *   onTimeTypeChange?: (value: string) => void,
 *   onStartDateChange?: (value: string) => void,
 *   onEndDateChange?: (value: string) => void,
 *   timeTypeOptions?: { value: string, label: string }[],
 *   showTimeType?: boolean,
 *   timeTypeLabel?: string,
 *   dateOnly?: boolean,
 *   disabled?: boolean,
 *   startPlaceholder?: string,
 *   endPlaceholder?: string,
 *   className?: string,
 *   timeTypeAriaLabel?: string,
 * }} props
 */
export default function TiktokDateRangeFilter({
  timeType = '',
  startDate = '',
  endDate = '',
  onTimeTypeChange,
  onStartDateChange,
  onEndDateChange,
  timeTypeOptions = TIKTOK_RETURN_TIME_TYPE_OPTIONS,
  showTimeType = true,
  timeTypeLabel = '',
  dateOnly = true,
  disabled = false,
  startPlaceholder = fixedUi("开始日期"),
  endPlaceholder = fixedUi("结束日期"),
  className = '',
  timeTypeAriaLabel = fixedUi("时间类型"),
}) {
  const options = Array.isArray(timeTypeOptions) ? timeTypeOptions : [];
  const resolvedType = timeType || options[0]?.value || '';

  return (
    <div
      className={`tko-field tko-field--inline tko-field--time-range${className ? ` ${className}` : ''}`}
    >
      {showTimeType && options.length > 0 ? (
        <select
          className="tko-select tko-select--compact tko-select--time-type"
          value={resolvedType}
          disabled={disabled}
          aria-label={timeTypeAriaLabel}
          onChange={(e) => onTimeTypeChange?.(e.target.value)}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      ) : timeTypeLabel ? (
        <span className="tko-time-range__type-label" aria-label={timeTypeAriaLabel || timeTypeLabel}>
          {timeTypeLabel}
        </span>
      ) : null}
      <TiktokDateTimePicker
        className="tko-date-range-filter__picker"
        dateOnly={dateOnly}
        value={startDate}
        disabled={disabled}
        allowClear
        placeholder={startPlaceholder}
        ariaLabel={startPlaceholder}
        onChange={(v) => onStartDateChange?.(v)}
      />
      <span className="tko-time-range__sep">{fixedUi("至")}</span>
      <TiktokDateTimePicker
        className="tko-date-range-filter__picker"
        dateOnly={dateOnly}
        value={endDate}
        disabled={disabled}
        allowClear
        placeholder={endPlaceholder}
        ariaLabel={endPlaceholder}
        onChange={(v) => onEndDateChange?.(v)}
      />
    </div>
  );
}
